import * as yup from 'yup';
import { Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';

/**
 * @param booksToAccountingSystem Fakturagruppen har en bokföringsintegration. Bara då gör
 * utbetalningsfälten någon nytta. Kunder som exporterar SIE-fil får inte utbetalningsverifikaten
 * än, och ska därför inte tvingas fylla i ett avgiftskonto som aldrig används.
 */
export const useSchemaInvoiceGroupAccount = (booksToAccountingSystem = false) => {
  const { t } = useTranslation(['invoice']);

  const optionalAccountField = yup
    .string()
    .optional()
    .matches(/^\d*$/, t('invoice:adminInvoiceGroupAccountingTab.validationNumbersOnly'))
    .max(6, t('invoice:adminInvoiceGroupAccountingTab.validationMaxLength'));

  // De tre Stripe-fälten hör ihop. Alla tomma betyder att utbetalningsbokföringen är avstängd,
  // och det ska gå att spara. Men fylls ett av dem i ser det påslaget ut utan att vara det, och
  // då blir kundens bokföring fel. Därför krävs alla tre så fort något av dem har ett värde.
  const stripeFieldsRequired = t('invoice:adminInvoiceGroupAccountingTab.stripeFieldsRequired');

  const invoiceGroupAccountSchema = yup.object().shape(
    {
      bankAccount: optionalAccountField,
      memberAccount: optionalAccountField,
      depositAccount: optionalAccountField,
      fsPayServiceAccount: optionalAccountField,
      voucherSerie: yup.string().optional(),
      eventAccount: optionalAccountField,
      buyAccount: optionalAccountField,
      clearingSwishAccount: optionalAccountField,
      excessAccount: optionalAccountField,
      clearingStripeAccount: optionalAccountField.when(['stripeFeeAccount', 'stripePayoutFromDate'], {
        is: (feeAccount: string, fromDate: Dayjs | null) => !!feeAccount || !!fromDate,
        then: (schema) => schema.required(stripeFieldsRequired),
      }),
      stripeFeeAccount: optionalAccountField.when(['clearingStripeAccount', 'stripePayoutFromDate'], {
        is: (clearingAccount: string, fromDate: Dayjs | null) => (booksToAccountingSystem && !!clearingAccount) || !!fromDate,
        then: (schema) => schema.required(stripeFieldsRequired),
      }),
      // Datumväljaren arbetar med Dayjs. Formatet YYYYMMDD sätts först när formuläret sparas,
      // eftersom det är så övriga datum i bokföringsflödet lagras.
      stripePayoutFromDate: yup
        .mixed<Dayjs>()
        .nullable()
        .optional()
        .when(['clearingStripeAccount', 'stripeFeeAccount'], {
          is: (clearingAccount: string, feeAccount: string) => (booksToAccountingSystem && !!clearingAccount) || !!feeAccount,
          then: (schema) => schema.required(stripeFieldsRequired),
        }),
    },
    // Fälten pekar på varandra, så varje par måste deklareras för yup.
    [
      ['clearingStripeAccount', 'stripeFeeAccount'],
      ['clearingStripeAccount', 'stripePayoutFromDate'],
      ['stripeFeeAccount', 'stripePayoutFromDate'],
    ],
  );
  return invoiceGroupAccountSchema;
};
